import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Zap, Plus, Loader2, Check, X, Trash2 } from "lucide-react";
import { fmtH } from "../lib/planning";

const STATUS_BADGES = {
    assegnata: { label: "ASSEGNATA", cls: "bg-sky-100 text-sky-700" },
    da_confermare: { label: "DA CONFERMARE", cls: "bg-amber-100 text-amber-700" },
    confermata: { label: "CONFERMATA", cls: "bg-emerald-100 text-emerald-700" },
    rifiutata: { label: "RIFIUTATA", cls: "bg-red-100 text-red-600" },
};
const fmtDate = (iso) => {
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
};

export default function StraordinariPage() {
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [worksites, setWorksites] = useState([]);
    const [filterStatus, setFilterStatus] = useState("");
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/extra", { params: filterStatus ? { status: filterStatus } : {} });
            setData(data);
        } catch { toast.error("Errore nel caricamento"); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/presenze/planning/employees").then((r) => setEmployees(r.data)).catch(() => {});
        api.get("/presenze/worksites").then((r) => setWorksites(r.data)).catch(() => {});
    }, []);

    const decide = async (x, status) => {
        try {
            await api.post(`/presenze/extra/${x.id}/decide`, { status });
            toast.success(status === "confermata" ? "Straordinario confermato" : "Straordinario rifiutato");
            load();
        } catch { toast.error("Errore"); }
    };

    const remove = async (x) => {
        if (!window.confirm(`Eliminare lo straordinario di ${x.employee_name}?`)) return;
        try {
            await api.delete(`/presenze/extra/${x.id}`);
            toast.success("Straordinario eliminato");
            load();
        } catch { toast.error("Errore nell'eliminazione"); }
    };

    if (!data) {
        return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    }

    return (
        <div className="mx-auto max-w-6xl px-6 py-8" data-testid="straordinari-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Zap className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Attività straordinarie</h1>
                        <p className="text-sm text-slate-500">Interventi extra assegnati dall'ufficio o segnalati dai dipendenti</p>
                    </div>
                </div>
                <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="extra-add-btn">
                    <Plus className="h-4 w-4" /> Nuovo straordinario
                </Button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
                    <h2 className="font-display text-sm font-bold uppercase tracking-wide text-slate-600">
                        Interventi {data.pending > 0 && <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="extra-pending-badge">{data.pending} da confermare</span>}
                    </h2>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="extra-filter-status">
                        <option value="">Tutti gli stati</option>
                        <option value="assegnata">Assegnati</option>
                        <option value="da_confermare">Da confermare</option>
                        <option value="confermata">Confermati</option>
                        <option value="rifiutata">Rifiutati</option>
                    </select>
                </div>
                {data.items.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500" data-testid="extra-empty">Nessuno straordinario presente.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="px-3 py-2.5">Data</th>
                                    <th className="px-3 py-2.5">Dipendente</th>
                                    <th className="px-3 py-2.5">Cantiere</th>
                                    <th className="px-3 py-2.5">Descrizione</th>
                                    <th className="px-3 py-2.5">Ore</th>
                                    <th className="px-3 py-2.5">Stato</th>
                                    <th className="px-3 py-2.5">Origine</th>
                                    <th className="px-3 py-2.5 text-right">Azioni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.items.map((x) => {
                                    const b = STATUS_BADGES[x.status] || {};
                                    return (
                                        <tr key={x.id} className="hover:bg-slate-50/60" data-testid={`extra-row-${x.id}`}>
                                            <td className="px-3 py-2 text-xs capitalize text-slate-600">{fmtDate(x.date)}</td>
                                            <td className="px-3 py-2 font-medium text-slate-700">{x.employee_name}</td>
                                            <td className="px-3 py-2 text-xs text-slate-600">{x.worksite_name || "—"}</td>
                                            <td className="max-w-[220px] truncate px-3 py-2 text-xs text-slate-600" title={x.description}>{x.description}</td>
                                            <td className="px-3 py-2 font-mono-numbers text-xs font-semibold text-slate-700">
                                                {fmtH(x.hours)} h
                                                {x.time_from && <span className="ml-1 font-normal text-slate-400">({x.time_from}–{x.time_to || "…"})</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${b.cls}`} data-testid={`extra-status-${x.id}`}>{b.label}</span>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-500">{x.source === "dipendente" ? "Dipendente" : x.source === "rls" ? "App RLS" : "Ufficio"}</td>
                                            <td className="px-3 py-2">
                                                <div className="flex justify-end gap-1">
                                                    {x.status === "da_confermare" && (
                                                        <>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" title="Conferma" onClick={() => decide(x, "confermata")} data-testid={`extra-confirm-${x.id}`}>
                                                                <Check className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" title="Rifiuta" onClick={() => decide(x, "rifiutata")} data-testid={`extra-reject-${x.id}`}>
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" title="Elimina" onClick={() => remove(x)} data-testid={`extra-delete-${x.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <CreateExtraDialog open={showCreate} employees={employees} worksites={worksites}
                onClose={() => setShowCreate(false)}
                onSaved={() => { setShowCreate(false); load(); }} />
        </div>
    );
}

function CreateExtraDialog({ open, employees, worksites, onClose, onSaved }) {
    const [employeeId, setEmployeeId] = useState("");
    const [worksiteId, setWorksiteId] = useState("");
    const [date, setDate] = useState("");
    const [hours, setHours] = useState("");
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setEmployeeId(""); setWorksiteId(""); setDate(""); setHours("");
            setTimeFrom(""); setTimeTo(""); setDescription("");
        }
    }, [open]);

    const save = async () => {
        const h = parseFloat(String(hours).replace(",", "."));
        if (!employeeId) { toast.error("Seleziona il dipendente"); return; }
        if (!date) { toast.error("Inserisci la data"); return; }
        if (Number.isNaN(h) || h <= 0) { toast.error("Inserisci le ore previste"); return; }
        if (!description.trim()) { toast.error("Inserisci la descrizione dell'intervento"); return; }
        setSaving(true);
        try {
            await api.post("/presenze/extra", {
                employee_id: employeeId, worksite_id: worksiteId || null,
                description: description.trim(), date, hours: h,
                time_from: timeFrom || null, time_to: timeTo || null,
            });
            toast.success("Straordinario assegnato");
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-md" data-testid="extra-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Nuovo straordinario</DialogTitle>
                    <DialogDescription>Assegna un intervento extra a un dipendente.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dipendente *</Label>
                        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                            className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="extra-employee-select">
                            <option value="">Seleziona…</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.cognome} {e.nome}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Data *</Label>
                            <Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="extra-date-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Ore previste * <span className="font-normal normal-case text-slate-400">(1,25 = 1h 15')</span></Label>
                            <Input type="number" min={0.01} step={0.01} className="mt-1" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="es. 1,25" data-testid="extra-hours-input" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Cantiere</Label>
                            <select value={worksiteId} onChange={(e) => setWorksiteId(e.target.value)}
                                className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="extra-worksite-select">
                                <option value="">Nessuno / altro</option>
                                {worksites.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dalle</Label>
                                <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)}
                                    className="mt-1 h-9 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="extra-time-from" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Alle</Label>
                                <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)}
                                    className="mt-1 h-9 w-full rounded-md border border-slate-200 px-1.5 text-sm" data-testid="extra-time-to" />
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Descrizione *</Label>
                        <Input className="mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Es. pulizia straordinaria post-lavori" data-testid="extra-desc-input" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="extra-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
